import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  exportMerchantOrders,
  formatMerchantApiError,
  getMerchantOrderReceipts,
  listMerchantOrders,
  serializeMerchantOrdersQuery,
} from "../../api";
import type {
  MerchantOrderItem,
  MerchantOrdersResponse,
} from "../../api";
import {useAuth} from "../../auth";
import {MerchantOrdersList} from "../../components";
import {navigate, useMerchantQuery, useRoute} from "../../hooks";
import {
  EMPTY_MERCHANT_ORDER_FILTERS,
  formatMerchantDate,
  hasMerchantOrderFilters,
  merchantFiltersToQuery,
  merchantOrdersHash,
  parseMerchantOrderFilters,
} from "../../utils";
import type {MerchantOrderFilterValues} from "../../utils";
import {
  merchantReceiptFilename,
  saveMerchantBlob,
} from "../../utils/merchantDownloads";

const ORDERS_PAGE_SIZE = 50;
const ATOMIC_AMOUNT_PATTERN = /^\d+$/;

interface OrdersPageRequest {
  key: string;
  response: MerchantOrdersResponse;
}

export function MerchantOrdersPage(): JSX.Element {
  const {token} = useAuth();
  const route = useRoute();
  const applied = useMemo(() => parseMerchantOrderFilters(route), [route]);
  const apiFilters = useMemo(() => merchantFiltersToQuery(applied), [applied]);

  const [draft, setDraft] = useState<MerchantOrderFilterValues>(applied);
  const [filterError, setFilterError] = useState("");
  const [items, setItems] = useState<MerchantOrderItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [downloadingReceiptOrderId, setDownloadingReceiptOrderId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState("");
  // На телефоне фильтры свёрнуты, пока ни один не задан: список заявок
  // начинается на первом экране. На широком экране кнопки нет.
  const [filtersOpen, setFiltersOpen] = useState(() => hasMerchantOrderFilters(applied));
  const exportRequestRef = useRef<AbortController | null>(null);
  const receiptRequestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setDraft(applied);
    setFilterError("");
    setItems([]);
    setOffset(0);
    setHasMore(false);
    setNextOffset(null);
  }, [applied]);

  useEffect(() => () => {
    exportRequestRef.current?.abort();
    receiptRequestRef.current?.abort();
  }, []);

  const requestKey = useMemo(
    () => `${serializeMerchantOrdersQuery({
      ...apiFilters,
      limit: ORDERS_PAGE_SIZE,
      offset,
    })}|reload=${reloadKey}`,
    [apiFilters, offset, reloadKey],
  );

  const request = useCallback(
    async (signal: AbortSignal): Promise<OrdersPageRequest> => ({
      key: requestKey,
      response: await listMerchantOrders(token, {
        ...apiFilters,
        limit: ORDERS_PAGE_SIZE,
        offset,
      }, signal),
    }),
    [apiFilters, offset, requestKey, token],
  );
  const query = useMerchantQuery(request, `merchant-orders:${requestKey}`, {
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (!query.data || query.data.key !== requestKey) return;
    const page = query.data.response;
    setItems((current) => (
      offset === 0 ? page.items : mergeOrders(current, page.items)
    ));
    setHasMore(page.hasMore);
    setNextOffset(page.nextOffset ?? null);
  }, [offset, query.data, requestKey]);

  const applyFilters = (event?: React.FormEvent): void => {
    event?.preventDefault();
    const problem = validateFilters(draft);
    if (problem) {
      setFilterError(problem);
      return;
    }
    setFilterError("");
    const destination = merchantOrdersHash(draft);
    if (destination === route) {
      reloadFirstPage();
    } else {
      navigate(destination);
    }
  };

  const clearFilters = (): void => {
    setFilterError("");
    const destination = merchantOrdersHash(EMPTY_MERCHANT_ORDER_FILTERS);
    if (destination === route) {
      setDraft({...EMPTY_MERCHANT_ORDER_FILTERS});
      reloadFirstPage();
    } else {
      navigate(destination);
    }
  };

  const reloadFirstPage = (): void => {
    setItems([]);
    setOffset(0);
    setHasMore(false);
    setNextOffset(null);
    setReloadKey((value) => value + 1);
  };

  const loadMore = (): void => {
    if (!hasMore || query.loading || query.refreshing) return;
    setOffset(nextOffset ?? offset + ORDERS_PAGE_SIZE);
  };

  const exportCSV = async (): Promise<void> => {
    exportRequestRef.current?.abort();
    const controller = new AbortController();
    exportRequestRef.current = controller;
    setExporting(true);
    setDownloadError("");
    try {
      const blob = await exportMerchantOrders(token, apiFilters, controller.signal);
      saveMerchantBlob(blob, "merchant-orders.csv");
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setDownloadError(formatMerchantApiError(error));
      }
    } finally {
      if (exportRequestRef.current === controller) {
        exportRequestRef.current = null;
        setExporting(false);
      }
    }
  };

  const downloadReceipts = async (item: MerchantOrderItem): Promise<void> => {
    receiptRequestRef.current?.abort();
    const controller = new AbortController();
    receiptRequestRef.current = controller;
    setDownloadingReceiptOrderId(item.orderId);
    setDownloadError("");
    try {
      const blob = await getMerchantOrderReceipts(
        token,
        item.orderId,
        controller.signal,
      );
      saveMerchantBlob(blob, merchantReceiptFilename(item.orderId, blob));
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setDownloadError(formatMerchantApiError(error));
      }
    } finally {
      if (receiptRequestRef.current === controller) {
        receiptRequestRef.current = null;
        setDownloadingReceiptOrderId(null);
      }
    }
  };

  const filtered = hasMerchantOrderFilters(applied);
  const busy = query.loading || query.refreshing;

  return (
    <div className="page merchant-page merchant-orders-page">
      <header className="page-header merchant-page-header">
        <div>
          <h1>Заявки</h1>
          <p className="page-subtitle">
            Все заявки вашей организации
          </p>
        </div>
        <div className="merchant-refresh-block">
          <span className="merchant-updated-at">
            {query.lastSuccessfulAt
              ? `Обновлено ${formatMerchantDate(query.lastSuccessfulAt)}`
              : "Данные ещё не загружены"}
          </span>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={reloadFirstPage}
            disabled={busy}
          >
            {busy ? "Обновление…" : "Обновить"}
          </button>
          <a className="btn btn-primary" href="#/merchant/orders/new">
            Создать заявку
          </a>
        </div>
      </header>

      <form className={`merchant-filters${filtersOpen ? " is-open" : ""}`} onSubmit={applyFilters}>
        <div className="merchant-filter-grid" id="merchant-order-filters">
          <label>
            <span>Внешний ID</span>
            <input
              type="search"
              value={draft.remoteId}
              placeholder="merchant-order-42"
              onChange={(event) => setDraft((current) => ({
                ...current,
                remoteId: event.target.value,
              }))}
            />
          </label>
          <label>
            <span>Статус</span>
            <select
              value={draft.status}
              onChange={(event) => setDraft((current) => ({
                ...current,
                status: event.target.value as MerchantOrderFilterValues["status"],
              }))}
            >
              <option value="">Все</option>
              <option value="NEW">Новая</option>
              <option value="PROCESS">В процессе</option>
              <option value="APPEAL">Спор</option>
              <option value="CANCEL">Отменена</option>
              <option value="OK">Выполнена</option>
            </select>
          </label>
          <label>
            <span>Направление</span>
            <select
              value={draft.side}
              onChange={(event) => setDraft((current) => ({
                ...current,
                side: event.target.value as MerchantOrderFilterValues["side"],
              }))}
            >
              <option value="">Все</option>
              <option value="out">Исходящая</option>
              <option value="in">Входящая</option>
            </select>
          </label>
          <label>
            <span>Создана с</span>
            <input
              type="date"
              value={draft.createdFrom}
              onChange={(event) => setDraft((current) => ({
                ...current,
                createdFrom: event.target.value,
              }))}
            />
          </label>
          <label>
            <span>Создана по</span>
            <input
              type="date"
              value={draft.createdTo}
              onChange={(event) => setDraft((current) => ({
                ...current,
                createdTo: event.target.value,
              }))}
            />
          </label>
          <label>
            <span>Сумма от, мин. единицы</span>
            <input
              type="text"
              inputMode="numeric"
              value={draft.minAmount}
              placeholder="Для RUB — копейки"
              onChange={(event) => setDraft((current) => ({
                ...current,
                minAmount: event.target.value,
              }))}
            />
          </label>
          <label>
            <span>Сумма до, мин. единицы</span>
            <input
              type="text"
              inputMode="numeric"
              value={draft.maxAmount}
              placeholder="Для RUB — копейки"
              onChange={(event) => setDraft((current) => ({
                ...current,
                maxAmount: event.target.value,
              }))}
            />
          </label>
        </div>
        {filterError && <div className="merchant-filter-error" role="alert">{filterError}</div>}
        <div className="merchant-filter-actions">
          <button
            className="btn btn-ghost merchant-filters-toggle"
            type="button"
            aria-expanded={filtersOpen}
            aria-controls="merchant-order-filters"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            {filtered ? "Фильтры · включены" : "Фильтры"}
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => void exportCSV()}
            disabled={exporting}
          >
            {exporting ? "Выгружаем…" : "Выгрузить CSV"}
          </button>
          <button className="btn btn-primary merchant-filter-apply" type="submit" disabled={busy}>
            Применить
          </button>
          <button className="btn btn-ghost merchant-filter-apply" type="button" onClick={clearFilters} disabled={busy}>
            Сбросить
          </button>
        </div>
      </form>

      {downloadError && (
        <div className="alert alert-warning" role="alert">
          {downloadError}
        </div>
      )}

      {query.error && items.length > 0 && (
        <div className="alert alert-warning merchant-stale-banner" role="status">
          {query.error} Уже загруженные строки сохранены.
          <button className="btn-link-clear" type="button" onClick={query.refresh}>
            Повторить
          </button>
        </div>
      )}

      {query.loading && items.length === 0 && (
        <div className="merchant-skeleton" aria-label="Загрузка заявок" aria-busy="true">
          <div className="merchant-skeleton-panel"/>
        </div>
      )}

      {!query.loading && items.length === 0 && query.error && (
        <section className="merchant-error-state" role="alert">
          <h2>Не удалось загрузить заявки</h2>
          <p>{query.error}</p>
          <button className="btn btn-primary" type="button" onClick={query.refresh}>
            Повторить
          </button>
        </section>
      )}

      {(!query.loading || items.length > 0) && !(!items.length && query.error) && (
        <section className="merchant-section-card merchant-orders-results">
          <div className="section-header merchant-section-header">
            <div>
              <h2>{filtered ? "Результаты фильтра" : "Все заявки"}</h2>
              <span>Загружено: {items.length}</span>
            </div>
          </div>
          <MerchantOrdersList
            items={items}
            onDownloadReceipts={(item) => void downloadReceipts(item)}
            downloadingReceiptOrderId={downloadingReceiptOrderId}
            emptyMessage={filtered
              ? "По выбранным фильтрам заявок нет."
              : "Заявок пока нет."}
          />
          {hasMore && (
            <div className="merchant-load-more">
              <button
                className="btn btn-ghost"
                type="button"
                onClick={loadMore}
                disabled={busy}
              >
                {busy ? "Загрузка…" : "Показать ещё"}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function mergeOrders(
  current: MerchantOrderItem[],
  next: MerchantOrderItem[],
): MerchantOrderItem[] {
  const byId = new Map(current.map((item) => [item.orderId, item]));
  for (const item of next) byId.set(item.orderId, item);
  return Array.from(byId.values());
}

function validateFilters(filters: MerchantOrderFilterValues): string {
  if (filters.createdFrom && filters.createdTo && filters.createdFrom > filters.createdTo) {
    return "Начало периода не может быть позже конца.";
  }
  if (filters.minAmount.trim() && !ATOMIC_AMOUNT_PATTERN.test(filters.minAmount.trim())) {
    return "Минимальная сумма должна быть целым числом в минимальных единицах валюты.";
  }
  if (filters.maxAmount.trim() && !ATOMIC_AMOUNT_PATTERN.test(filters.maxAmount.trim())) {
    return "Максимальная сумма должна быть целым числом в минимальных единицах валюты.";
  }
  return "";
}
